<template>
  <div class="c-card-selector">
    <div class="c-card-selector__header">
      <h1 id="c-card-selector__header-label">{{ title }}</h1>
      <p v-html="description"></p>
    </div>
    <div class="c-card-selector__filters">
      <div v-if="!filters">
        <slot name="filters" />
      </div>
      <aha-card-selector-filter
        v-else
        v-for="(item, i) in filters"
        :title="item.title"
        :description="item.description"
        :name="item.name"
        :checked="isFiltered(item.name)"
        :key="`filter_${i}`"
      />
    </div>
    <div v-if="errors" class="c-card-selector__message">{{ errors[0] }}</div>
    <ul
      class="c-card-selector__content"
      ref="cardSelectorNode"
      role="listbox"
      aria-labelledby="c-card-selector__header-label"
      aria-multiselectable="true"
      tabindex="-1"
    >
      <li
        v-for="(item, i) in sortedItems"
        class="c-card-selector-item"
        :aria-selected="isSelected(item.id) ? 'true' : 'false'"
        :key="`selector-item_${i}`"
        :id="`selector-item_${i}`"
        :ref="`selectorItem_${i}`"
        role="option"
        @mouseover="focusedItem = item.id"
        @mouseleave="focusedItem = null"
        :active="focusedItem === item.id"
        @click="(evt) => toggleItem(evt, item.id)"
        @keypress.enter="(evt) => toggleItem(evt, item.id)"
        @blur="focusedItem = null"
        @focus="focusedItem = item.id"
        tabindex="0"
      >
        <aha-selection-card
          :id="item.id"
          :title="item.title"
          :img="item.img"
          :imgAlt="item.imgAlt"
          :description="item.description"
          :button="item.button"
          :removeButtonText="item.removeButtonText"
          :flags="item.flags"
          :categoryTitle="item.categoryTitle"
          :mediaTitle="item.mediaTitle"
          :mediaText="item.mediaText"
          :mediaLink="item.mediaLink"
          :mediaLinkTarget="item.mediaLinkTarget"
          :mediaLinkClass="item.mediaLinkClass"
          :mediaLinkDescription="item.mediaLinkDescription"
          :downloadLink="item.downloadLink"
          :downloadLinkTarget="item.downloadLinkTarget"
          :downloadLinkClass="item.downloadLinkClass"
          :downloadButtonText="item.downloadButtonText"
          :selected="isSelected(item.id)"
          :active="focusedItem === item.id"
          :sortOrder="item.sortOrder"
        ></aha-selection-card>
      </li>
      <div v-if="!items">
        <slot />
      </div>
    </ul>
    <div class="c-card-selector__actions">
      <slot
        name="actions"
        v-bind:valid="$store.state.selectedIds.length > 0"
        v-bind:submit="submit"
        v-bind:isFiltered="isFiltered"
        v-bind:selectedItems="$store.state.selectedIds"
      />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { forEach } from "lodash";
import { initStore } from "../store/modules/card-selector";
import { mapState, mapGetters, mapActions } from "vuex";
import { KeyCodes } from "bootstrap-vue/src/utils";

export default {
  provide() {
    return {
      EventBus: this.EventBus,
    };
  },
  data() {
    return {
      EventBus: new Vue(),
      active: -1,
      isSubmitted: false,
      focusedItem: null,
      filters: [],
      ...mapState({
        selectedFilters: (state) => state.filters,
      }),
    };
  },
  props: {
    name: { type: String },
    data: { type: String },
    title: { type: String },
    description: { type: String },
  },
  beforeCreate() {
    this.$store = initStore();
    this.$store.commit("initialiseStore");
  },
  init() {
    this.isSubmitted = false;
  },

  mounted() {
    if (this.$scopedSlots.default && this.$scopedSlots.default()) {
      const items = []; // Array
      _.forEach(this.$scopedSlots.default(), (element) => {
        if (element.tag && element.tag.endsWith("aha-selection-card")) {
          const elData = element.componentOptions.propsData;
          items.push(elData); // Push item to array - items
        }
      });
      _.forEach(this.$scopedSlots.filters(), (element) => {
        if (element.tag && element.tag.endsWith("aha-card-selector-filter")) {
          const elData = element.componentOptions.propsData;
          this.filters.push(elData); // Push to array - filters
        }
      });

      // Converting sortOrder into array
      for (const item of items) {
        item.sortOrder = item.sortOrder.split(",");
      }

      this.$store.commit("setItems", items); // Add items to Vuex Store
    }

    this.EventBus.$on("filterChanged", (name, isSelected) => {
      this.$store.commit("updateFilter", { name, isSelected });
      this.resize();
    });
    this.resize();
  },
  computed: {
    ...mapGetters({
      items: "availableItems",
      selectedItems: "selectedItems",
    }),
    sortedItems: function () {
      let _this = this;

      function compare(a, b) {
        let num1, num2;
        if (
          _this.selectedFilters().includes("youth") &&
          a.flags.length > 1 &&
          a.flags.includes("adults") &&
          a.flags.includes("youth")
        ) {
          num1 = Number(a.sortOrder[1]);
        } else {
          num1 = Number(a.sortOrder[0]);
        }
        if (
          _this.selectedFilters().includes("youth") &&
          b.flags.length > 1 &&
          b.flags.includes("adults") &&
          b.flags.includes("youth")
        ) {
          num2 = Number(b.sortOrder[1]);
        } else {
          num2 = Number(b.sortOrder[0]);
        }

        if (num1 < num2) return -1;
        if (num1 > num2) return 1;
        return 0;
      }
      return this.items.sort(compare);
    },
  },
  methods: {
    resize() {
      const container = this.$refs["cardSelectorNode"];
      const items = container.getElementsByClassName("c-selection-card");
      const itemContainers = container.getElementsByClassName(
        "c-card-selector-item"
      );
      let height = 0;
      let padding;
      const getPadding = (el) => {
        if (padding) return padding;

        const styles = window.getComputedStyle(el, null);
        const px = styles["paddingTop"];
        const pt = parseInt(styles["paddingTop"]);
        const pb = parseInt(styles["paddingBottom"]);
        padding = pt + pb;
        return padding;
      };
      forEach(items, (card) => {
        if (height < card.offsetHeight) {
          height = card.offsetHeight;

          console.log('inside selector');
          console.log('height', height);
          console.log('card.offsetHeight', card.offsetHeight)
        }

        if(height > 350){
          height = 350;
        }
      });

      forEach(itemContainers, (cardContainer) => {
        const styles = window.getComputedStyle(cardContainer, null);
        cardContainer.style["min-height"] = `${
          height + getPadding(cardContainer)
        }px`;
  
      });
    },
    isSelected(id) {
      return this.$store.state.selectedIds.includes(id);
    },
    onKeyPress(evt) {
      var key = evt.which || evt.keyCode;
      let previousItem = null,
        nextItem = null,
        currentItem = null;
      if (!this.focusedItem) {
        return;
      }
      this.items.forEach((item, i) => {
        if (item.id === this.focusedItem) {
          currentItem = item;
          previousItem = i > 0 ? this.items[i - 1].id : null;
          nextItem = this.items.length - 1 > i ? this.items[i + 1].id : null;
        }
      });

      // console.log("navitems", currentItem, previousItem, nextItem);
      switch (key) {
        case KeyCodes.UP:
          evt.preventDefault();
          previousItem && this.focusItem(previousItem);
          break;
        case KeyCodes.DOWN:
          evt.preventDefault();
          nextItem && this.focusItem(nextItem);
          break;
        case KeyCodes.SPACE:
          evt.preventDefault();
          this.toggleItem(evt, currentItem);
          break;
      }
    },
    toggleItem(evt, id) {
      const isSelected = !this.$store.state.selectedIds.includes(id);
      this.$store.commit("itemSelectionChanged", { id, isSelected });
      evt.preventDefault();
    },
    isFiltered(name) {
      return this.$store.state.filters.includes(name);
    },
    focusItem(itemId) {
      if (itemId) {
        this.focusedItem = itemId;
      } else {
        if (this.$store.state.selectedIds.length > 0) {
          this.focusedItem = this.$store.state.selectedIds[0];
        } else {
          this.focusedItem = this.$store.state.items[0].id;
        }
      }
    },
    submit(event) {
      this.$root.$emit("bv::show::modal", "loadingModal");
      this.isSubmitted = true;
      setTimeout(() => {
        let newUri = event.target.attributes.href.nodeValue;
        window.location.href = newUri;
      }, 250);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/app/components/aha-card-selector.vue